<template>
  <div>
    <v-expansion-panels v-model="expandedPanels" multiple>
      <!-- Source Level Filter: hidden while sources have no level (HIDDEN.level) -->
      <v-expansion-panel v-if="!HIDDEN.level" value="level">
        <v-expansion-panel-title>
          <v-icon start>mdi-layers</v-icon>
          Уровень источников
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-checkbox
            v-model="filters.include_federal"
            label="Федеральный уровень"
            density="compact"
            hide-details
            class="mb-2"
          ></v-checkbox>
          <v-checkbox
            v-model="filters.include_regional"
            label="Региональный уровень"
            density="compact"
            hide-details
            class="mb-2"
          ></v-checkbox>
          <v-checkbox
            v-model="filters.include_foreign_close"
            label="Ближнее зарубежье"
            density="compact"
            hide-details
            class="mb-2"
          ></v-checkbox>
          <v-checkbox
            v-model="filters.include_foreign_far"
            label="Дальнее зарубежье"
            density="compact"
            hide-details
            class="mb-2"
          ></v-checkbox>
          <v-checkbox
            v-model="filters.include_global"
            label="Глобальные источники"
            density="compact"
            hide-details
          ></v-checkbox>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Geolocations Filter -->
      <v-expansion-panel value="geo">
        <v-expansion-panel-title>
          <v-icon start>mdi-map-marker</v-icon>
          Гео
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-autocomplete
            v-model="filters.include_geolocations"
            :items="groupedGeolocations"
            :loading="geolocationsLoading"
            :search="geoSearch"
            @update:search="geoSearch = $event"
            @update:model-value="geoSearch = ''"
            label="Выберите геолокации"
            :hint="HIDDEN.subCountryGeo ? 'Страны' : 'Страны, округа, регионы, города'"
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="name"
            item-value="id"
            clearable
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                size="small"
                closable
                :color="getGeoColor(item.raw.level)"
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
            <template v-slot:item="{ props, item }">
              <v-list-subheader
                v-if="item.raw.header"
                :title="item.raw.header"
              ></v-list-subheader>
              <v-list-item
                v-else
                v-bind="props"
                :title="item.raw.name"
              >
                <template v-slot:prepend>
                  <v-icon size="small" :color="getGeoColor(item.raw.level)">
                    {{ getGeoIcon(item.raw.level) }}
                  </v-icon>
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Platforms Filter -->
      <v-expansion-panel value="platforms">
        <v-expansion-panel-title>
          <v-icon start>mdi-share-variant</v-icon>
          Платформы
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-autocomplete
            v-model="filters.include_platforms"
            :items="platformItems"
            :loading="platformsLoading"
            label="Выберите платформы"
            hint="Только платформы, которые собираются: Telegram, X.com, Дзен и другие"
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="title"
            item-value="id"
            clearable
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                size="small"
                closable
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Media Kinds Filter: hidden while sources have no media kind (HIDDEN.mediaKinds) -->
      <v-expansion-panel v-if="!HIDDEN.mediaKinds" value="media-kinds">
        <v-expansion-panel-title>
          <v-icon start>mdi-newspaper</v-icon>
          Типы медиа
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-autocomplete
            v-model="filters.include_media_kinds"
            :items="mediaKindsList"
            :loading="mediaKindsLoading"
            label="Выберите типы источников"
            hint="Печатные издания, ТВ, радио, интернет-издания и т.п."
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="name"
            item-value="id"
            clearable
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                size="small"
                closable
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Topics Filter -->
      <v-expansion-panel value="topics">
        <v-expansion-panel-title>
          <v-icon start>mdi-tag-multiple</v-icon>
          Тематика источников
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-autocomplete
            v-model="filters.include_topics"
            :items="topicsList"
            :loading="topicsLoading"
            label="Выберите тематики"
            hint="Общественно-политические, деловые, развлекательные и т.п."
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="name"
            item-value="id"
            clearable
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                size="small"
                closable
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Languages Filter -->
      <v-expansion-panel value="languages">
        <v-expansion-panel-title>
          <v-icon start>mdi-translate</v-icon>
          Языки сообщений
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-autocomplete
            v-model="filters.include_languages"
            :items="languagesList"
            :loading="languagesLoading"
            label="Выберите языки"
            hint="Русский, английский, и другие"
            persistent-hint
            multiple
            chips
            closable-chips
            variant="outlined"
            item-title="name"
            item-value="id"
            clearable
          >
            <template v-slot:chip="{ props, item }">
              <v-chip
                v-bind="props"
                size="small"
                closable
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-autocomplete>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Help Text -->
    <v-alert
      type="info"
      variant="tonal"
      density="compact"
      class="mt-4"
    >
      <template v-slot:text>
        <strong>Фильтры источников:</strong> Выберите критерии для фильтрации источников информации.
        Если ничего не выбрано в категории, будут включены все источники этой категории.
      </template>
    </v-alert>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import apiClient from '@/apiClient';

// Filters shown only when the data behind them exists (18.10.2026: 0 % of the messages of 30 days
// carried a level or a media kind of their source; with a media kind chosen a project found nothing).
// core project_filter_resolver.py ignores them too while they are hidden.
const HIDDEN = { level: true, mediaKinds: true, subCountryGeo: true };

// Props and emits
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      include_federal: true,
      include_regional: true,
      include_foreign_close: true,
      include_foreign_far: true,
      include_global: true,
      include_media_kinds: [],
      include_platforms: [],
      include_topics: [],
      include_geolocations: [],
      include_languages: [],
    }),
  },
  openPanels: {
    type: Array,
    default: () => ['level'],
  },
});

const emit = defineEmits(['update:modelValue', 'update:openPanels']);

// Refs
const geoSearch = ref('');
const expandedPanels = ref([...props.openPanels]);

// Sync expandedPanels back to parent
watch(expandedPanels, (newVal) => {
  emit('update:openPanels', newVal);
}, { deep: true });
const filters = ref({
  include_federal: true,
  include_regional: true,
  include_foreign_close: true,
  include_foreign_far: true,
  include_global: true,
  include_media_kinds: [],
  include_platforms: [],
  include_topics: [],
  include_geolocations: [],
  include_languages: [],
});

// Reference data lists
const platformsList = ref([]);
const platformsLoading = ref(false);
const mediaKindsList = ref([]);
const mediaKindsLoading = ref(false);
const topicsList = ref([]);
const topicsLoading = ref(false);
const geolocationsList = ref([]);
const geolocationsLoading = ref(false);
const languagesList = ref([]);
const languagesLoading = ref(false);

// Geo level labels and order
const geoLevelConfig = {
  country:  { label: 'Страны',              icon: 'mdi-earth',        color: 'blue'   },
  district: { label: 'Федеральные округа',  icon: 'mdi-map',          color: 'teal'   },
  region:   { label: 'Регионы',             icon: 'mdi-map-marker',   color: 'orange' },
  city:     { label: 'Города',              icon: 'mdi-city',         color: 'purple' },
};
// Telegram channels have only a country; districts, regions and cities are set for Dzen only, and
// picking a city dropped all of Telegram (18.10.2026) - countries only for now
const geoLevelOrder = HIDDEN.subCountryGeo ? ['country'] : ['country', 'district', 'region', 'city'];

// Grouped geolocations for v-autocomplete with headers
// Platforms without collection (ВКонтакте, YouTube, Facebook, Instagram: is_monitoring=false) are
// not offered (task 52); one already chosen in a project stays visible so it can be removed
const platformItems = computed(() => platformsList.value
  .filter(p => p.is_monitoring || (filters.value.include_platforms || []).includes(p.id))
  .map(p => ({ id: p.id, title: p.display_name || p.name })));

const groupedGeolocations = computed(() => {
  const result = [];
  for (const level of geoLevelOrder) {
    const items = geolocationsList.value.filter(g => g.level === level);
    if (items.length === 0) continue;

    const config = geoLevelConfig[level] || { label: level };
    // Add group header (type: 'subheader' for Vuetify)
    result.push({ header: config.label, divider: true });
    // Add items sorted by name
    const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    for (const item of sorted) {
      result.push({ ...item, level });
    }
  }
  return result;
});

const getGeoColor = (level) => geoLevelConfig[level]?.color || 'grey';
const getGeoIcon = (level) => geoLevelConfig[level]?.icon || 'mdi-map-marker';

// Initialize filters from props
if (props.modelValue) {
  filters.value = { ...filters.value, ...props.modelValue };
}

// Watch for prop changes (only update if different to avoid loops)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && JSON.stringify(newValue) !== JSON.stringify(filters.value)) {
      filters.value = { ...filters.value, ...newValue };
    }
  },
  { deep: true }
);

// Watch for filter changes (only emit if different to avoid loops)
let isUpdating = false;
watch(
  filters,
  (newValue) => {
    if (!isUpdating && JSON.stringify(newValue) !== JSON.stringify(props.modelValue)) {
      isUpdating = true;
      emit('update:modelValue', newValue);
      setTimeout(() => { isUpdating = false; }, 0);
    }
  },
  { deep: true }
);

// Methods
const fetchPlatforms = async () => {
  platformsLoading.value = true;

  try {
    const response = await apiClient.get('/ref/platforms');
    platformsList.value = response.data.items || response.data || [];
  } catch (error) {
    console.error('Error fetching platforms:', error);
    platformsList.value = [];
  } finally {
    platformsLoading.value = false;
  }
};

const fetchMediaKinds = async () => {
  mediaKindsLoading.value = true;

  try {
    const response = await apiClient.get('/ref/media-kinds');
    mediaKindsList.value = response.data.items || response.data || [];
  } catch (error) {
    console.error('Error fetching media kinds:', error);
    mediaKindsList.value = [
      { id: 1, name: 'Печатные издания' },
      { id: 2, name: 'Инфоагентства' },
      { id: 3, name: 'Интернет-издания' },
      { id: 4, name: 'ТВ' },
      { id: 5, name: 'Радио' },
      { id: 6, name: 'Агрегаторы' },
    ];
  } finally {
    mediaKindsLoading.value = false;
  }
};

const fetchTopics = async () => {
  topicsLoading.value = true;

  try {
    const response = await apiClient.get('/ref/topics');
    topicsList.value = response.data.items || response.data || [];
  } catch (error) {
    console.error('Error fetching topics:', error);
    topicsList.value = [
      { id: 1, name: 'Общественно-политические' },
      { id: 2, name: 'Деловые' },
      { id: 3, name: 'Развлекательные' },
      { id: 4, name: 'Научные' },
      { id: 5, name: 'Спортивные' },
    ];
  } finally {
    topicsLoading.value = false;
  }
};

const fetchGeolocations = async () => {
  geolocationsLoading.value = true;

  try {
    const response = await apiClient.get('/ref/geolocations', { params: { limit: 500 } });
    geolocationsList.value = response.data.items || response.data || [];
  } catch (error) {
    console.error('Error fetching geolocations:', error);
    geolocationsList.value = [
      { id: 1, name: 'Центральный ФО', level: 'district' },
      { id: 2, name: 'Москва', level: 'city' },
      { id: 3, name: 'Санкт-Петербург', level: 'city' },
      { id: 4, name: 'Россия', level: 'country' },
    ];
  } finally {
    geolocationsLoading.value = false;
  }
};

const fetchLanguages = async () => {
  languagesLoading.value = true;

  try {
    const response = await apiClient.get('/ref/languages');
    languagesList.value = response.data.items || response.data || [];
  } catch (error) {
    console.error('Error fetching languages:', error);
    languagesList.value = [
      { id: 1, name: 'Русский' },
      { id: 2, name: 'Английский' },
      { id: 3, name: 'Немецкий' },
      { id: 4, name: 'Французский' },
      { id: 5, name: 'Не определен' },
    ];
  } finally {
    languagesLoading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchPlatforms();
  fetchMediaKinds();
  fetchTopics();
  fetchGeolocations();
  fetchLanguages();
});
</script>

<style scoped>
/* Component-specific styles */
</style>
